/**
 * Performance context card with percentile comparison
 * Displays percentile for all metrics the user has entered
 * Shows sample size (n=X) and insufficient data message when needed
 */

import { Card } from '@/components/ui/Card';
import { colors } from '@/lib/design-tokens';

// ============================================================================
// TYPES
// ============================================================================

export interface PercentileInfo {
  value: number;
  percentile: number;
  label: string;
  higherIsBetter: boolean | null;
  sampleSize: number;
}

export interface PerformanceContextData {
  hasMetrics: boolean;
  message: string | null;
  percentiles: Record<string, PercentileInfo> | null;
  sampleSize: number;
}

export interface PerformanceContextCardProps {
  data: PerformanceContextData;
  onAddMetrics?: () => void;
  isLoading?: boolean;
}

// ============================================================================
// HELPERS
// ============================================================================

function getPercentileColor(percentile: number, higherIsBetter: boolean | null): string {
  if (higherIsBetter === null) return '#9CA3AF'; // gray-400 — neutral
  const effectivePercentile = higherIsBetter ? percentile : 100 - percentile;

  if (effectivePercentile >= 75) {
    return colors.status.success;
  } else if (effectivePercentile >= 50) {
    return colors.status.warning;
  } else if (effectivePercentile >= 25) {
    return '#F97316'; // Orange
  } else {
    return colors.status.danger;
  }
}

function getPercentileLabel(percentile: number, higherIsBetter: boolean | null): string {
  if (higherIsBetter === null) return `${percentile}th pctl`;
  const effectivePercentile = higherIsBetter ? percentile : 100 - percentile;

  if (effectivePercentile >= 75) {
    return 'Top Performer';
  } else if (effectivePercentile >= 50) {
    return 'Above Average';
  } else if (effectivePercentile >= 25) {
    return 'Below Average';
  } else {
    return 'Needs Improvement';
  }
}

function formatValue(value: number, label: string): string {
  // Format based on metric type
  if (label.includes('%') || label.toLowerCase().includes('rate') || label.toLowerCase().includes('ctr')) {
    return `${value.toFixed(2)}%`;
  } else if (label.toLowerCase().includes('cpc') || label.toLowerCase().includes('cpm') || label.toLowerCase().includes('spend')) {
    return `$${value.toFixed(2)}`;
  } else if (label.toLowerCase().includes('impressions')) {
    return value.toLocaleString();
  }
  return String(value);
}

// ============================================================================
// COMPONENT
// ============================================================================

export function PerformanceContextCard({
  data,
  onAddMetrics,
  isLoading = false,
}: PerformanceContextCardProps) {
  // Loading state
  if (isLoading) {
    return (
      <Card className="p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Performance Context</h3>
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-4 bg-gray-200 rounded w-1/2" />
          <div className="h-4 bg-gray-200 rounded w-2/3" />
        </div>
      </Card>
    );
  }

  // No metrics entered yet
  if (!data.hasMetrics) {
    return (
      <Card className="p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Performance Context</h3>
        <div className="text-center py-4">
          <svg
            className="w-12 h-12 mx-auto text-gray-300 mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <p className="text-sm text-gray-600 mb-4">
            {data.message || 'Add performance metrics to see how this creative compares to others.'}
          </p>
          {onAddMetrics && (
            <button
              onClick={onAddMetrics}
              className="text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              Add Performance Metrics
            </button>
          )}
        </div>
      </Card>
    );
  }

  // Has metrics but insufficient data for percentiles
  if (!data.percentiles) {
    return (
      <Card className="p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Performance Context</h3>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <p className="text-sm text-yellow-800">
                {data.message || 'Insufficient data for percentile comparison (need 5+ creatives)'}
              </p>
              <p className="text-xs text-yellow-600 mt-1">
                Currently {data.sampleSize} creative{data.sampleSize !== 1 ? 's' : ''} with metrics in this project
              </p>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // Has percentiles - display the comparison
  const metrics = Object.entries(data.percentiles);

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">Performance Context</h3>
        <span className="text-xs text-gray-500">n={data.sampleSize}</span>
      </div>

      <div className="space-y-4">
        {metrics.map(([key, info]) => (
          <div key={key} className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">{info.label}</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-gray-900">
                  {formatValue(info.value, info.label)}
                </span>
              </div>
            </div>

            {/* Percentile Bar */}
            <div className="relative">
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${info.percentile}%`,
                    backgroundColor: getPercentileColor(info.percentile, info.higherIsBetter),
                  }}
                />
              </div>
              {/* Percentile marker */}
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white shadow"
                style={{
                  left: `${Math.min(Math.max(info.percentile, 5), 95)}%`,
                  transform: 'translateX(-50%) translateY(-50%)',
                  backgroundColor: getPercentileColor(info.percentile, info.higherIsBetter),
                }}
              />
            </div>

            <div className="flex items-center justify-between text-xs">
              <span
                className="font-medium"
                style={{ color: getPercentileColor(info.percentile, info.higherIsBetter) }}
              >
                {getPercentileLabel(info.percentile, info.higherIsBetter)}
              </span>
              <span className="text-gray-500">
                {info.percentile}th percentile
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="mt-6 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
          <div className="flex items-center gap-1.5">
            <div
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: colors.status.success }}
            />
            <span>Top 25%</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: colors.status.warning }}
            />
            <span>50-75%</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: '#F97316' }}
            />
            <span>25-50%</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: colors.status.danger }}
            />
            <span>Bottom 25%</span>
          </div>
        </div>
      </div>
    </Card>
  );
}
